import { Button, Col, Divider, Drawer, Dropdown, Menu, Result, Row, Space, Spin } from "antd";
import { FC, useEffect, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import InfoList from "../../../../components/infoList";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import LayoutStore from "../../../../contexts/layoutStore";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import _t from "../../../../lang/translate";
import { downloadTaxOffer, getSavedOfferUrl } from "../../../../services/taxService";
import { useApi } from "../../../../services/useApi";
import { ApiResponseObject } from "../../../../types/apiTypes";
import { TaxScenario } from "../../../../types/taxTypes";
import consts from "../../../../utilities/consts";
import format from "../../../../utilities/formatNumbers";
import { createTaxScenario } from "../../../../services/taxScenarioService";
import { getScenarioUpsertPayload } from "../taxFlow/util";

interface OfferHistoryDrawerProps {
  optMode?: boolean;
}

const TaxOfferArchived: FC<OfferHistoryDrawerProps> = ({ optMode = false }) => {
  const { screen } = LayoutStore.useContainer();
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const [loading, setLoading] = useState(false)
  const [visible, setVisible] = useState<boolean>(false);
  const [{ data, isError, isLoading }, setUrl] = useApi<ApiResponseObject<TaxScenario[]>>("", { data: [] });
  const id = taxCase.id;
  const scenario = taxCase.scenarios[optMode ? "optimization" : "offer"]

  useEffect(() => {
    if (visible) setUrl(getSavedOfferUrl(id, optMode));
  }, [visible, setUrl, id, optMode]);

  const openDrawer = () => setVisible(true);
  const closeDrawer = () => setVisible(false);

  async function downloadOffer(documentId: number) {
    try {
      setLoading(true)
      const offer = await downloadTaxOffer(taxCase.id, documentId)
      if (offer.data.url) window.open(offer.data.url, '_blank')
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }

  async function applyOffer(offer: TaxScenario) {
    try {
      setLoading(true)
      const payload = getScenarioUpsertPayload(offer.calculation ?? {}, optMode)
      const { data: newTaxCase } = await createTaxScenario(taxCase.id, payload)
      setTaxCase(newTaxCase)
      setVisible(false);
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button tabIndex={-1} onClick={openDrawer}>
        <Icon name="file-tray-full-outline" size="medium" /> {_t("archive")}
      </Button>
      <Drawer
        footer={null}
        title={
          <Row align="middle" justify="space-between">
            <Col>
              {_t("saved_offers")}
            </Col>
            <Col>
              {/* NOTE: The purpose of ?update= is only to trigger useEffect */}
              <Button onClick={() => { setUrl(getSavedOfferUrl(id, optMode) + "?update=" + Date.now()) }}
                shape="circle"
                type="text"
                icon={<Icon size="large" name="sync-outline" className={isLoading ? "animate-spin" : ""} />}
              />
            </Col>
          </Row>
        }
        width={screen.isMobile ? consts.drawerWidthSm : consts.drawerWidth}
        visible={visible}
        onClose={closeDrawer}
        className="calculation-table"
      >
        {isError && <Result status="error" />}
        {isLoading && <Spin size="large" />}
        {!data?.data?.length && (
          <Result
            status="404"
            title="Du har ingen gemte beregninger endnu"
            subTitle="Opret en beregning og klik på 'Gem som ny version'. Den vil derefter blive vist her."
          />
        )}
        {data.data &&
          data.data.toSorted((a, b) => (b.id ?? -Infinity) - (a.id ?? -Infinity)).map((offer, i) => {
            const actionsMenu = (
              <Menu>
                <Menu.Item key="downloadOffer"
                  disabled={!!optMode}
                  icon={<Icon size="large" name="download-outline" />}
                  onClick={() => offer.id && downloadOffer(offer.id)}
                >
                  {_t("fetch")}
                </Menu.Item>
              </Menu>
            )
            return (
              <Row className="position-relative" gutter={[16, 16]} key={i}>
                <Col span={24}>
                  {offer?.person && (
                    <p className="text-center">
                      <strong>v{offer.version} - {offer.person?.fullName}</strong>
                      <a href={"mailto:" + offer.person.email} className="d-block">
                        {offer.person.email}
                      </a>
                    </p>
                  )}
                  <InfoList className="bg-white position-relative">
                    <InfoList.Title title={format.dateAndTime(offer?.createdAt)} />
                    <InfoList.Item value={format.dateAndTime(offer?.updatedAt)} label={_t("updated_at")} />
                    <InfoList.Item value={format.price(offer.calculation?.newPrice?.taxableValue)} label={_t("taxable_value")} />
                    <InfoList.Item value={format.price(offer.calculation?.newPrice?.newPrice)} label={_t("new_price")} />
                    <InfoList.Item value={format.price(offer.calculation?.tradePrice?.tradePrice)} label={_t("trade_price")} />
                    <InfoList.Item
                      value={format.price(offer.calculation?.registrationFee?.registrationFee)}
                      label={_t("registration_fee")}
                    />
                    <InfoList.Item value={format.price(offer.licensePlateFee)} label={_t("plade_nr_fee")} />
                    <InfoList.Item value={format.price(offer?.calculation?.offer?.caseFee)} label={_t("fee")} />
                    {optMode ? (
                      <InfoList.Item value={format.price(offer?.calculation?.offer?.collateral?.total)} label={_t("collateral")} />
                    ) : (
                      <InfoList.Item value={format.price(offer?.calculation?.offer?.collateral?.total)} label={_t("estimated", "collateral")} />
                    )}
                    <Divider className="mb-1 mt-2" />
                  </InfoList>

                  <Dropdown.Button overlay={actionsMenu}
                    onClick={() => applyOffer(offer)}
                    style={{ position: "absolute", right: 0, bottom: 48 }}
                    disabled={scenario?.id === offer.id}
                    loading={loading}
                  >
                    {_t("apply")}
                  </Dropdown.Button>
                </Col>
              </Row>
            )
          })}
      </Drawer>
    </>
  );
};

export default TaxOfferArchived;
